/**
 * Module-scope API error bus (no React).
 *
 * `createApiClient`'s response interceptor is a plain module function and
 * cannot call `useToast()`. It emits failures here instead, and
 * `ApiErrorToaster` — mounted inside the app's existing `ToastProvider` —
 * subscribes and renders an error toast.
 *
 * Identical messages emitted within `DEDUPE_WINDOW_MS` are collapsed, so a
 * dashboard firing several calls against a failing backend surfaces one toast
 * rather than a stack of duplicates.
 */

/** A user-facing API failure. */
export interface ApiErrorEvent {
  /**
   * Human-readable, user-facing message.
   * Never include tokens, credentials or raw URLs.
   */
  message: string;
  /** HTTP status when a response was received; `undefined` for network errors. */
  status?: number;
}

type ApiErrorListener = (event: ApiErrorEvent) => void;

/** Window (ms) during which an identical message is collapsed into one event. */
export const DEDUPE_WINDOW_MS = 3000;

const listeners = new Set<ApiErrorListener>();

/** message -> timestamp of the last emission, for deduping. */
const lastEmittedAt = new Map<string, number>();

/**
 * Subscribes to API error events.
 *
 * @param listener - Called for every non-deduped error event.
 * @returns An unsubscribe function.
 */
export function subscribeApiError(listener: ApiErrorListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Emits an API error event to all subscribers.
 *
 * Identical messages within `DEDUPE_WINDOW_MS` are dropped.
 *
 * @param event - The failure to surface.
 * @returns `true` when the event was dispatched, `false` when deduped.
 */
export function emitApiError(event: ApiErrorEvent): boolean {
  const now = Date.now();
  const previous = lastEmittedAt.get(event.message);

  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return false;
  }

  // Drop stale entries so the map cannot grow unbounded over a long session.
  for (const [message, at] of lastEmittedAt) {
    if (now - at >= DEDUPE_WINDOW_MS) {
      lastEmittedAt.delete(message);
    }
  }
  lastEmittedAt.set(event.message, now);

  for (const listener of listeners) {
    listener(event);
  }
  return true;
}

/**
 * Clears all listeners and the dedupe window.
 *
 * Test seam — the application never resets the bus.
 */
export function resetApiErrorBus(): void {
  listeners.clear();
  lastEmittedAt.clear();
}
